import { EquipmentCard } from '@/app/equipment/EquipmentCard';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { type currentHomeState, equipmentTypesByIdState, homeEquipmentListState, type spacesState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { Space, SpaceId, SpaceSummaryStatus } from '@rock/types';
import type { useAtomValue } from 'jotai';
import { orderBy, startCase } from 'lodash';
import { Loader2 } from 'lucide-react';
import type { FC } from 'react';
import { useMemo } from 'react';

export const HomeEquipmentTab: FC<{
    home: NonNullable<ReturnType<typeof useAtomValue<typeof currentHomeState>>>;
    spaces: ReturnType<typeof useAtomValue<typeof spacesState>>;
}> = ({ home, spaces }) => {
    const orderedSpaces = useMemo(() => {
        return orderBy(spaces, (space) => space.name.toLowerCase());
    }, [spaces]);

    const spaceSummaryById = useMemo(() => {
        const summaries: Record<SpaceId, { status: SpaceSummaryStatus; blockCount: number }> = {};
        for (const [spaceId, summary] of Object.entries(home?.spaceSummary ?? {})) {
            summaries[spaceId as SpaceId] = summary as { status: SpaceSummaryStatus; blockCount: number };
        }
        return summaries;
    }, [home?.spaceSummary]);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Spaces &amp; equipment</CardTitle>
                <CardDescription>Review the equipment that has been set up in each space.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {orderedSpaces.length === 0 ? (
                    <div className="section-description">No spaces are available for this home yet.</div>
                ) : (
                    orderedSpaces.map((space) => (
                        <SpaceEquipmentSection key={space.id} space={space} summary={spaceSummaryById[space.id]} />
                    ))
                )}
            </CardContent>
        </Card>
    );
};

type SpaceEquipmentSectionProps = {
    space: Space;
    summary?: { status: SpaceSummaryStatus; blockCount: number };
};

const SpaceEquipmentSection: FC<SpaceEquipmentSectionProps> = ({ space, summary }) => {
    const [equipment, equipmentLoadable] = useLoadableAtomValue(homeEquipmentListState(space.id), []);
    // Kept only for the combined loading/error gate below; EquipmentCard resolves its own type.
    const [, equipmentTypesLoadable] = useLoadableAtomValue(equipmentTypesByIdState(space.id), {});

    const hasError = equipmentLoadable.state === 'hasError' || equipmentTypesLoadable.state === 'hasError';
    const isLoading =
        !hasError && (equipmentLoadable.state === 'loading' || equipmentTypesLoadable.state === 'loading');

    const introText = space.intro?.[0]?.text;

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-1">
                    <h3 className="text-lg font-semibold text-foreground">{space.name}</h3>
                    {introText ? <div className="line-clamp-2 section-description">{introText}</div> : null}
                </div>
                {summary ? (
                    <Badge variant={getStatusBadgeVariant(summary.status)}>{startCase(summary.status)}</Badge>
                ) : null}
            </div>
            {hasError ? (
                <div className="text-destructive text-sm">Unable to load equipment details for this space.</div>
            ) : isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading equipment…
                </div>
            ) : equipment.length === 0 ? (
                <div className="section-description">No equipment has been configured for this space yet.</div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2">
                    {equipment.map((item) => (
                        <EquipmentCard key={item.id} equipment={item} />
                    ))}
                </div>
            )}
        </div>
    );
};

const getStatusBadgeVariant = (status: SpaceSummaryStatus) => {
    switch (status) {
        case 'finished':
            return 'default' as const;
        case 'in-progress':
            return 'secondary' as const;
        default:
            return 'outline' as const;
    }
};
